import { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useReturnSale } from '@/api/hooks/useCreateSale';
import type { Sale } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { money } from '@/lib/utils';

interface LineState {
  medicineId: string;
  name: string;
  unitPrice: number;
  maxQty: number;
  include: boolean;
  qty: string;
}

export function ReturnDialog({
  sale,
  open,
  onOpenChange,
}: {
  sale: Sale | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const returnSale = useReturnSale();

  const [lines, setLines] = useState<LineState[]>([]);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && sale) {
      setLines(
        sale.items
          .filter((item) => item.medicineId)
          .map((item) => ({
            medicineId: item.medicineId!,
            name: item.medicineName ?? 'Unknown',
            unitPrice: item.unitPrice,
            maxQty: item.quantity,
            include: true,
            qty: String(item.quantity),
          })),
      );
      setReason('');
      setError(null);
    }
  }, [open, sale]);

  if (!sale) return null;

  function toggleLine(medicineId: string) {
    setLines((prev) =>
      prev.map((line) => (line.medicineId === medicineId ? { ...line, include: !line.include } : line)),
    );
  }

  function setQty(medicineId: string, value: string) {
    setLines((prev) => prev.map((line) => (line.medicineId === medicineId ? { ...line, qty: value } : line)));
  }

  async function handleSubmit() {
    setError(null);

    const items = lines
      .filter((line) => line.include && Number(line.qty) > 0)
      .map((line) => ({ medicineId: line.medicineId, quantity: Math.min(Number(line.qty), line.maxQty) }));

    if (items.length === 0) {
      setError('Select at least one item to return.');
      return;
    }

    try {
      const result = await returnSale.mutateAsync({
        saleId: sale!.id,
        request: { items, reason: reason.trim() || undefined },
      });
      toast({
        title: 'Return processed',
        description: `Refund: ${money(result.refundAmount)}`,
        variant: 'success',
      });
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.code === 'ALREADY_RETURNED' ? err.message : err.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Return — {sale.invoiceNo}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Check items to return. Stock is restored automatically.
        </p>

        <div className="space-y-2">
          {lines.map((line) => (
            <div key={line.medicineId} className="flex items-center gap-3 rounded-md bg-secondary/50 p-2.5">
              <input
                type="checkbox"
                checked={line.include}
                onChange={() => toggleLine(line.medicineId)}
                className="h-4 w-4 shrink-0 accent-primary"
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{line.name}</p>
                <p className="tabular text-xs text-muted-foreground">
                  {money(line.unitPrice)} × {line.maxQty}
                </p>
              </div>
              <Input
                type="number"
                min={1}
                max={line.maxQty}
                value={line.qty}
                disabled={!line.include}
                onChange={(e) => setQty(line.medicineId, e.target.value)}
                className="h-8 w-16 text-center"
              />
            </div>
          ))}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="return-reason">Reason</Label>
          <Input
            id="return-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Wrong medicine, not needed"
          />
        </div>

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={returnSale.isPending}>
            {returnSale.isPending && <LoaderCircle className="animate-spin" />}
            Process return & refund
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
